import React, { useEffect, useState } from 'react'
import {
  getReferenceChecks,
  createReferenceCheck,
  updateReferenceCheck,
  sendReferenceTypeform,
  getReferenceTypeformSubmission,
  ReferenceCheckInput,
  TapTypeformSubmission,
} from '@/api/tapResources'
import { TapReferenceCheck } from '@/types/index'
import { Modal } from '@/components/ui/Modal'
import { Plus, Send, RotateCw, Eye, FileText } from 'lucide-react'
import { formatDateTime } from '@/utils/dateFormat'

// Feature #157 — Reference Checks tab.
interface ReferenceChecksTabProps {
  applicationId: string
}

// Feature #219: 'Typeform' added so agents can pick it as the contact
// method and trigger the templated invite. Order kept stable so existing
// values stay where users expect them.
const CONTACT_METHODS = ['Phone', 'Email', 'Video Call', 'In Person', 'Typeform']
const OUTCOMES = ['Positive', 'Neutral', 'Concern']

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-white)',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '8px',
  padding: '20px',
  marginBottom: '16px',
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '6px',
  fontSize: '14px',
  fontFamily: 'var(--font-family)',
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '12px',
  fontWeight: 600,
  color: 'var(--color-dark-grey)',
  textTransform: 'uppercase',
  marginBottom: '4px',
}

const primaryBtn: React.CSSProperties = {
  padding: '8px 16px',
  backgroundColor: 'var(--color-magenta)',
  color: 'var(--color-white)',
  border: 'none',
  borderRadius: '6px',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'var(--font-family)',
}
const secondaryBtn: React.CSSProperties = {
  ...primaryBtn,
  backgroundColor: 'var(--color-white)',
  color: 'var(--color-black)',
  border: '1px solid var(--color-light-grey)',
}

const outcomeColour = (outcome?: string): string => {
  switch (outcome) {
    case 'Positive': return '#16a34a'
    case 'Concern': return '#dc2626'
    case 'Neutral': return '#d97706'
    default: return 'var(--color-dark-grey)'
  }
}

const ReferenceChecksTab: React.FC<ReferenceChecksTabProps> = ({ applicationId }) => {
  const [items, setItems] = useState<TapReferenceCheck[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<TapReferenceCheck | null>(null)
  const [saving, setSaving] = useState(false)

  // Feature #219: Typeform send + view-submission state.
  // ``sendingId`` tracks which row's send button is in flight so we
  // disable it (and only it) while the request is open. ``submission``
  // holds the Q&A returned by the view endpoint and feeds the modal.
  const [sendingId, setSendingId] = useState<string | null>(null)
  const [submissionFor, setSubmissionFor] = useState<TapReferenceCheck | null>(null)
  const [submission, setSubmission] = useState<TapTypeformSubmission | null>(null)
  const [submissionLoading, setSubmissionLoading] = useState(false)

  const [refereeName, setRefereeName] = useState('')
  const [refereeRelationship, setRefereeRelationship] = useState('')
  const [refereeOrganisation, setRefereeOrganisation] = useState('')
  const [refereeJobTitle, setRefereeJobTitle] = useState('')
  const [refereeEmail, setRefereeEmail] = useState('')
  const [contactMethod, setContactMethod] = useState('')
  const [feedback, setFeedback] = useState('')
  const [outcome, setOutcome] = useState('')

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      setItems(await getReferenceChecks(applicationId))
    } catch {
      setError('Unable to load reference checks')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId])

  const openCreate = () => {
    setEditing(null)
    setRefereeName('')
    setRefereeRelationship('')
    setRefereeOrganisation('')
    setRefereeJobTitle('')
    setRefereeEmail('')
    setContactMethod('')
    setFeedback('')
    setOutcome('')
    setModalOpen(true)
  }

  const openEdit = (rc: TapReferenceCheck) => {
    setEditing(rc)
    setRefereeName(rc.refereeName)
    setRefereeRelationship(rc.refereeRelationship || '')
    setRefereeOrganisation(rc.refereeOrganisation || '')
    setRefereeJobTitle(rc.refereeJobTitle || '')
    setRefereeEmail(rc.refereeEmail || '')
    setContactMethod(rc.contactMethod || '')
    setFeedback(rc.feedback || '')
    setOutcome(rc.outcome || '')
    setModalOpen(true)
  }

  // Feature #219: send (or resend) the Typeform invite for this row.
  // The button is gated client-side (visible only when contact_method is
  // Typeform AND referee_email is set); we still surface server-side 4xx
  // errors so the agent gets a useful message if the row drifts.
  const handleSendTypeform = async (rc: TapReferenceCheck) => {
    if (!rc.refereeEmail) {
      alert('Add a referee email before sending the Typeform invite.')
      return
    }
    setSendingId(rc.id)
    try {
      const updated = await sendReferenceTypeform(applicationId, rc.id)
      // Optimistically swap the updated row in so the "Sent X ago" hint
      // appears without needing a full refetch — but also kick off a
      // background reload to pick up any timeline events written by the
      // backend (e.g. the new ReferenceTypeformSent row).
      setItems((prev) => prev.map((r) => (r.id === rc.id ? updated : r)))
      load()
    } catch (err) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      alert(typeof detail === 'string' ? `Failed to send: ${detail}` : 'Failed to send Typeform invite')
    } finally {
      setSendingId(null)
    }
  }

  // Feature #219: open the View Submission modal — fetches the Q&A on
  // demand so we don't load every reference's payload on tab open.
  const handleViewSubmission = async (rc: TapReferenceCheck) => {
    setSubmissionFor(rc)
    setSubmission(null)
    setSubmissionLoading(true)
    try {
      const data = await getReferenceTypeformSubmission(applicationId, rc.id)
      setSubmission(data)
    } catch {
      // Modal stays open showing the loading-failed empty state. The
      // submission viewer is a read-only secondary action — surface the
      // failure inline rather than a blocking alert.
      setSubmission(null)
    } finally {
      setSubmissionLoading(false)
    }
  }

  const handleSave = async () => {
    if (!refereeName.trim()) {
      alert('Referee name is required')
      return
    }
    setSaving(true)
    try {
      const payload: ReferenceCheckInput = {
        refereeName,
        refereeRelationship: refereeRelationship || undefined,
        refereeOrganisation: refereeOrganisation || undefined,
        refereeJobTitle: refereeJobTitle || undefined,
        refereeEmail: refereeEmail || undefined,
        contactMethod: contactMethod || undefined,
        feedback: feedback || undefined,
        outcome: outcome || undefined,
      }
      if (editing) {
        await updateReferenceCheck(applicationId, editing.id, payload)
      } else {
        await createReferenceCheck(applicationId, payload)
      }
      setModalOpen(false)
      await load()
    } catch {
      alert('Failed to save reference check')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>Reference Checks</h3>
        <button onClick={openCreate} style={{ ...primaryBtn, display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <Plus size={14} /> Add reference check
        </button>
      </div>

      {loading && <div style={{ color: 'var(--color-dark-grey)' }}>Loading…</div>}
      {error && <div style={{ color: 'var(--color-magenta)' }}>{error}</div>}

      {!loading && !error && items.length === 0 && (
        <div style={cardStyle}>
          <div style={{ color: 'var(--color-dark-grey)' }}>No reference checks recorded yet.</div>
        </div>
      )}

      {!loading && items.map((rc) => (
        <div key={rc.id} style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: '15px', fontWeight: 600 }}>{rc.refereeName}</div>
              <div style={{ fontSize: '13px', color: 'var(--color-dark-grey)', marginTop: '2px' }}>
                {[rc.refereeJobTitle, rc.refereeOrganisation].filter(Boolean).join(' · ') || '—'}
              </div>
              {rc.refereeRelationship && (
                <div style={{ fontSize: '13px', color: 'var(--color-dark-grey)', marginTop: '2px' }}>
                  Relationship: {rc.refereeRelationship}
                </div>
              )}
              {rc.refereeEmail && (
                <div style={{ fontSize: '13px', color: 'var(--color-dark-grey)', marginTop: '2px' }}>
                  {rc.refereeEmail}
                </div>
              )}
              {rc.contactMethod && (
                <div style={{ fontSize: '12px', color: 'var(--color-dark-grey)', marginTop: '2px' }}>
                  Contact via {rc.contactMethod}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
              {rc.outcome && (
                <span style={{
                  padding: '4px 10px',
                  borderRadius: '999px',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: outcomeColour(rc.outcome),
                  border: `1px solid ${outcomeColour(rc.outcome)}`,
                }}>
                  {rc.outcome}
                </span>
              )}
              <button onClick={() => openEdit(rc)} style={secondaryBtn}>Edit</button>
            </div>
          </div>
          {rc.feedback && (
            <div style={{ marginTop: '10px', fontSize: '13px', whiteSpace: 'pre-wrap' }}>
              {rc.feedback}
            </div>
          )}

          {/* Feature #219: Typeform invite + response controls. Three states:
              - not yet sent: blue "Send Reference Check Form" button (disabled
                until referee_email is set);
              - sent, no response yet: muted "Sent at" hint + Resend button;
              - response received: green "Response received at" hint + View
                Submission button. */}
          {rc.contactMethod === 'Typeform' && (
            <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', fontSize: '13px' }}>
              {!rc.typeformSentAt && (
                <button
                  onClick={() => handleSendTypeform(rc)}
                  style={{ ...primaryBtn, display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                  disabled={sendingId === rc.id || !rc.refereeEmail}
                  title={!rc.refereeEmail ? 'Add a referee email first' : ''}
                >
                  <Send size={14} />
                  {sendingId === rc.id ? 'Sending…' : 'Send Reference Check Form'}
                </button>
              )}
              {rc.typeformSentAt && !rc.typeformCompletedAt && (
                <>
                  <span style={{ color: 'var(--color-dark-grey)' }}>
                    Sent {formatDateTime(rc.typeformSentAt)}
                  </span>
                  <button
                    onClick={() => handleSendTypeform(rc)}
                    style={{ ...secondaryBtn, display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                    disabled={sendingId === rc.id}
                  >
                    <RotateCw size={14} />
                    {sendingId === rc.id ? 'Resending…' : 'Resend'}
                  </button>
                </>
              )}
              {rc.typeformCompletedAt && (
                <>
                  <span style={{ color: '#047857', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    <FileText size={14} /> Response received {formatDateTime(rc.typeformCompletedAt)}
                  </span>
                  <button
                    onClick={() => handleViewSubmission(rc)}
                    style={{ ...secondaryBtn, display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                  >
                    <Eye size={14} /> View Submission
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      ))}

      <Modal
        isOpen={modalOpen}
        title={editing ? 'Edit reference check' : 'Add reference check'}
        onClose={() => setModalOpen(false)}
        isDirty={refereeName !== '' || refereeRelationship !== '' || refereeEmail !== '' || feedback !== '' || outcome !== ''}
        footer={
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button onClick={() => setModalOpen(false)} style={secondaryBtn}>Cancel</button>
            <button onClick={handleSave} style={primaryBtn} disabled={saving || !refereeName.trim()}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        }
      >
        <div style={{ display: 'grid', gap: '12px' }}>
          <div>
            <label style={labelStyle}>Referee name *</label>
            <input value={refereeName} onChange={(e) => setRefereeName(e.target.value)} style={inputStyle} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={labelStyle}>Relationship</label>
              <input value={refereeRelationship} onChange={(e) => setRefereeRelationship(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Organisation</label>
              <input value={refereeOrganisation} onChange={(e) => setRefereeOrganisation(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Job title</label>
              <input value={refereeJobTitle} onChange={(e) => setRefereeJobTitle(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Email</label>
              <input type="email" value={refereeEmail} onChange={(e) => setRefereeEmail(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Contact method</label>
              <select
                value={contactMethod}
                onChange={(e) => setContactMethod(e.target.value)}
                style={{ ...inputStyle, backgroundColor: 'var(--color-white)', cursor: 'pointer' }}
              >
                <option value="">—</option>
                {CONTACT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Outcome</label>
              <select
                value={outcome}
                onChange={(e) => setOutcome(e.target.value)}
                style={{ ...inputStyle, backgroundColor: 'var(--color-white)', cursor: 'pointer' }}
              >
                <option value="">—</option>
                {OUTCOMES.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label style={labelStyle}>Feedback</label>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows={6}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>
        </div>
      </Modal>

      {/* Feature #219: View Submission modal. Same Q&A layout as the
          application-side typeform-submission viewer — read-only list
          of question/answer pairs. */}
      <Modal
        isOpen={Boolean(submissionFor)}
        title={submissionFor ? `Reference response — ${submissionFor.refereeName}` : 'Reference response'}
        onClose={() => { setSubmissionFor(null); setSubmission(null) }}
        size="lg"
      >
        <div style={{ maxHeight: '70vh', overflowY: 'auto', padding: '0 4px' }}>
          {submissionLoading && (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--color-dark-grey)' }}>
              Loading submission…
            </div>
          )}
          {!submissionLoading && !submission && (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--color-dark-grey)' }}>
              Couldn't load the Typeform response for this reference check.
            </div>
          )}
          {!submissionLoading && submission && (
            <>
              {submission.submittedAt && (
                <div style={{ marginBottom: '16px', fontSize: '13px', color: 'var(--color-dark-grey)' }}>
                  Submitted: {formatDateTime(submission.submittedAt)}
                </div>
              )}
              <div style={{ display: 'grid', gap: '14px' }}>
                {submission.items.map((item, idx) => (
                  <div key={idx}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-dark-grey)', textTransform: 'uppercase', marginBottom: '4px' }}>
                      {item.question}
                    </div>
                    <div style={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}>
                      {item.answer || <span style={{ color: 'var(--color-dark-grey)' }}>—</span>}
                    </div>
                  </div>
                ))}
                {submission.items.length === 0 && (
                  <div style={{ color: 'var(--color-dark-grey)', fontSize: '13px' }}>
                    No answers in this submission.
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  )
}

export default ReferenceChecksTab
